import React from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  BellAlertIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../auth/firebaseAuth';

export interface ToolFeature {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  route: string;
  status: 'available' | 'coming-soon';
  badge?: string;
  stats?: { label: string; value: string | number }[];
}

interface AvailableFeaturesProps {
  features: ToolFeature[];
  onFeatureClick: (feature: ToolFeature) => void;
}

const AvailableFeatures: React.FC<AvailableFeaturesProps> = ({
  features,
  onFeatureClick
}) => {
  const availableFeatures = features.filter((f) => f.status === 'available');

  return (
    <section className="py-2">
      <div className="container mx-auto px-2">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Available Tools
          </h2>
          <p className="text-gray-600">Click on any tool to start using it</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl">
          {availableFeatures.map((feature) => (
            <div
              key={feature.id}
              onClick={() => onFeatureClick(feature)}
              className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all border-2 border-blue-200 hover:border-blue-400 cursor-pointer transform hover:scale-105"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 rounded-xl bg-blue-100 group-hover:bg-blue-200 transition-colors">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                </div>
                {feature.badge && (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                    {feature.badge}
                  </span>
                )}
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                {feature.title}
              </h3>

              <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                {feature.description}
              </p>

              {/* Stats */}
              {feature.stats && (
                <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t border-gray-200">
                  {feature.stats.map((stat, index) => (
                    <div key={index}>
                      <div className="text-2xl font-bold text-blue-600">
                        {stat.value}
                      </div>
                      <div className="text-xs text-gray-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* CTA */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-sm font-medium text-blue-600 group-hover:text-blue-700">
                  Open Tool
                </span>
                <ArrowRightIcon className="w-5 h-5 text-blue-600 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AvailableFeatures;

// Self-contained section component with internal features + navigation
export const AvailableFeaturesSection: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const features: ToolFeature[] = [
    {
      id: 'rent-tracker',
      title: 'Rent Payment Tracker',
      description:
        'Track all rent payments across your properties. View payment status, send reminders, and manage tenancies from one dashboard.',
      icon: ClipboardDocumentListIcon,
      route: '/property-tools/rent-tracker',
      status: 'available',
      badge: 'Most Popular'
    },
    {
      id: 'reminders',
      title: 'Automated Reminders',
      description:
        'Control which automated reminders RentSmart sends to your tenants and turn all reminders off if needed.',
      icon: BellAlertIcon,
      route: '/landlord-toolkit/reminders',
      status: 'available'
    },
    {
      id: 'rent-review-calculator',
      title: 'Rent Review Calculator',
      description:
        'Calculate allowable rent changes using RTB HICP logic. Uses the most recently available HICP for compliance.',
      icon: ChartBarIcon,
      route: '/property-tools/rent-review-calculator',
      status: 'available',
      badge: 'New'
    },
    {
      id: 'maintenance-requests',
      title: 'Maintenance Requests',
      description: 'View and manage maintenance requests from your tenants.',
      icon: WrenchScrewdriverIcon,
      route: '/landlord-dashboard/maintenance',
      status: 'available'
    },
    {
      id: 'expense-tracker',
      title: 'Expense Tracker',
      description:
        'Track rental income and deductible expenses. Upload receipts and categorize costs for tax purposes.',
      icon: ChartBarIcon,
      route: '/property-tools/expense-tracker',
      status: 'available',
      badge: 'New'
    }
  ];

  const handleFeatureClick = (feature: ToolFeature) => {
    if (feature.status !== 'available') return;
    // If user not authenticated, redirect to login with returnUrl to landlord toolkit
    if (!currentUser) {
      navigate(`/login?returnUrl=${encodeURIComponent('/landlord-toolkit')}`);
      return;
    }
    navigate(feature.route);
  };

  return (
    <AvailableFeatures
      features={features}
      onFeatureClick={handleFeatureClick}
    />
  );
};
